import { useTranslation } from 'react-i18next';
import { Boxes, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCurrentUser } from '@/lib/auth-context';

import { AFFECTS_FLAGS, affectsFor, canAuthorWorkPackages, resolveI18nText } from './helpers';
import { useAssignWorkPackage, useCompanyScope, useWorkPackagesList } from './useWorkPackages';

interface ApplyWorkPackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleCode: string;
  /** Restrict the offered packages to a party type (the role's own). */
  partyType?: string | null;
  /** Fired after a package is successfully assigned to the role. */
  onApplied?: () => void;
}

export const ApplyWorkPackageDialog = ({
  open,
  onOpenChange,
  roleCode,
  partyType,
  onApplied,
}: ApplyWorkPackageDialogProps) => {
  const { t, i18n } = useTranslation('settings');
  const user = useCurrentUser();
  const isPlatformActor =
    canAuthorWorkPackages(user) ||
    user?.partyType === 'platform' ||
    (user?.permissions ?? []).includes('system.admin');
  const query = useWorkPackagesList();
  const assign = useAssignWorkPackage();

  // For non-platform actors, fetch the company scope and restrict to enabled packages.
  // Platform/system users keep seeing all active packages (FR-001 backend gate).
  // Fail closed: non-platform users see an empty list until scope data is confirmed loaded.
  const scopeQuery = useCompanyScope(!isPlatformActor ? (user?.companyId ?? undefined) : undefined);
  // enabledIds is only populated once scopeQuery.data is available (never null=allow-all).
  // Before load or on error, it remains an empty Set so no packages pass the filter.
  const enabledIds = new Set(
    (scopeQuery.data ?? []).filter((s) => s.enabled).map((s) => s.work_package_id),
  );

  const isLoading = query.isLoading || (!isPlatformActor && scopeQuery.isLoading);
  const scopeError = !isPlatformActor && !!scopeQuery.error;

  const packages = (query.data ?? [])
    .filter((pkg) => pkg.active_flag)
    .filter((pkg) => !partyType || !pkg.party_type || pkg.party_type === partyType)
    .filter((pkg) => isPlatformActor || enabledIds.has(pkg.work_package_id));

  const apply = async (workPackageId: string) => {
    await assign.mutateAsync({ id: workPackageId, body: { role_code: roleCode } });
    onApplied?.();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.settings.work_packages.apply.title')}</SheetTitle>
          <SheetDescription>{t('efs.settings.work_packages.apply.hint')}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            <p className="text-sm text-efs-text-muted">
              {t('efs.common.messages.loading', { ns: 'common' })}
            </p>
          ) : scopeError ? (
            <p className="text-sm text-destructive">
              {t('efs.settings.work_packages.apply.scope_error')}
            </p>
          ) : packages.length === 0 ? (
            <p className="text-sm text-efs-text-muted">
              {t('efs.settings.work_packages.apply.empty')}
            </p>
          ) : (
            packages.map((pkg) => {
              const active = AFFECTS_FLAGS.filter((flag) => affectsFor(pkg, flag));
              return (
                <div
                  key={pkg.work_package_id}
                  className="rounded-md border border-efs-border px-3 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-efs-text-primary">
                        <Boxes className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {resolveI18nText(pkg.name_i18n, i18n.language) || pkg.package_code}
                        </span>
                      </div>
                      <p className="mt-1 break-all text-xs text-efs-text-muted">
                        {pkg.package_code}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {active.length > 0 ? (
                          active.map((flag) => (
                            <Badge key={flag} variant="secondary">
                              {t(`efs.settings.work_packages.affects.${flag}`)}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-efs-text-muted">
                            {t('efs.settings.work_packages.apply.no_effects')}
                          </span>
                        )}
                        <Badge variant="outline">
                          {t('efs.settings.work_packages.summary.permissions', {
                            count: pkg.permission_codes.length,
                          })}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={assign.isPending}
                      onClick={() => void apply(pkg.work_package_id)}
                    >
                      {assign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {t('efs.settings.work_packages.apply.confirm')}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
          {assign.error ? <p className="text-sm text-destructive">{assign.error.message}</p> : null}
        </div>

        <SheetFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
